import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Stack, Row, Text, Badge, Panel, Button, EmptyState } from '../ui';

function fmt(amount: number | null | undefined, currency = 'USD'): string {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

export function EntityDetail() {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editNotes, setEditNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!entityId) return;
    setLoading(true);
    api.entity(entityId).then(r => { setData(r); setNotes(r.entity.notes ?? ''); }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const saveNotes = async () => {
    if (!entityId) return;
    setSaving(true);
    try { await api.patchEntity(entityId, { notes }); setEditNotes(false); load(); }
    catch (e: any) { alert(e.message); } finally { setSaving(false); }
  };

  if (loading) return <Text muted variant="small">loading...</Text>;
  if (!data) return <EmptyState title="entity not found" />;
  const { entity, totalDeals, dealHistory } = data;

  return (
    <Stack gap={3}>
      <Row gap={2} align="center">
        <button onClick={() => navigate('/crm')} className="text-muted text-sm hover:text-ink">CRM</button>
        <span className="text-muted text-sm">/</span>
        <Text variant="small">{entity.displayName}</Text>
      </Row>

      <div className="hairline rounded-sm p-3 sm:p-4">
        <Stack gap={2}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <Stack gap={1} className="min-w-0 flex-1">
              <Text variant="display">{entity.displayName}</Text>
              <Row gap={2} wrap>
                <Badge tone="accent">{entity.kind}</Badge>
                {entity.country && <Text muted variant="small">{entity.country}</Text>}
                {entity.primaryContactEmail && <Text muted variant="small" className="truncate">{entity.primaryContactEmail}</Text>}
              </Row>
            </Stack>
            <Text muted variant="small" className="sm:text-right shrink-0">
              <span className="font-semibold text-ink tnum">{totalDeals}</span> deal{totalDeals !== 1 ? 's' : ''}
            </Text>
          </div>
          <Stack gap={1}>
            <Row gap={2} align="center">
              <Text muted variant="label">notes</Text>
              {!editNotes && <button className="text-xs text-muted hover:text-ink" onClick={() => setEditNotes(true)}>edit</button>}
            </Row>
            {editNotes ? (
              <Stack gap={1}>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full hairline rounded-sm px-2 py-1.5 text-sm bg-bg resize-none" placeholder="add notes..." />
                <Row gap={1} justify="end">
                  <Button variant="secondary" size="sm" onClick={() => { setEditNotes(false); setNotes(entity.notes ?? ''); }}>cancel</Button>
                  <Button variant="primary" size="sm" onClick={saveNotes} disabled={saving}>{saving ? 'saving...' : 'save'}</Button>
                </Row>
              </Stack>
            ) : <Text variant="small" className={entity.notes ? '' : 'text-muted italic'}>{entity.notes || 'no notes'}</Text>}
          </Stack>
        </Stack>
      </div>

      <Panel title={`deal history - ${totalDeals} total`}>
        {dealHistory.length === 0 ? <EmptyState title="no deals linked" hint="run backfill or link deals manually" />
          : <Stack gap={0}>
              {dealHistory.map((d: any, i: number) => d.restricted ? (
                <div key={i} className="py-2 px-1 hairline-b flex items-center gap-2 text-sm text-muted">
                  <span>🔒</span><span className="italic">1 additional deal — payment-restricted</span>
                </div>
              ) : (
                <div key={d.dealId} className="py-2 px-1 hairline-b flex items-center gap-3 text-sm cursor-pointer hover:bg-whisper" onClick={() => navigate(`/deals/${d.dealId}`)}>
                  <Badge tone="accent">{d.stage}</Badge>
                  <span className="font-medium flex-1 truncate">{d.name}</span>
                  <span className="text-muted tnum">{fmt(d.ticketSize, d.currency)}</span>
                  {d.role && <span className="text-xs text-muted">{d.role}</span>}
                  {d.myPaymentAmount != null && <span className="text-xs text-positive font-medium">you received: {fmt(d.myPaymentAmount, d.currency)}</span>}
                </div>
              ))}
            </Stack>}
      </Panel>
    </Stack>
  );
}
